/**
 * The OpenAPI document, read by the app's own API reference page.
 *
 * Deliberately NOT routed through `api.get`: `/api/v1/openapi.json` is served
 * outside the API stack (it is public, so a credential cannot be refused for
 * asking what the surface is), and it answers with the document itself rather
 * than the `{ data, requestId }` envelope every other endpoint uses. The
 * shared client unwraps that envelope and would reject this as malformed.
 *
 * The types below describe only the parts the reference page renders. That is
 * a deliberate floor rather than a half-finished ceiling: the document is
 * generated from the Worker's own surface table, so the page can rely on the
 * shapes that generator actually emits, and anything it stops emitting shows
 * up as an empty section rather than a crash.
 */

export interface OpenApiSchema {
  type?: string;
  format?: string;
  description?: string;
  enum?: unknown[];
  default?: unknown;
  minimum?: number;
  maximum?: number;
  items?: OpenApiSchema;
  properties?: Record<string, OpenApiSchema>;
  required?: string[];
  anyOf?: OpenApiSchema[];
  oneOf?: OpenApiSchema[];
  allOf?: OpenApiSchema[];
}

export interface OpenApiParameter {
  name: string;
  in: 'path' | 'query' | 'header' | 'cookie';
  required?: boolean;
  description?: string;
  schema?: OpenApiSchema;
}

export interface OpenApiOperation {
  operationId?: string;
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: OpenApiParameter[];
  security?: Array<Record<string, string[]>>;
  requestBody?: {
    required?: boolean;
    content?: Record<string, { schema?: OpenApiSchema; example?: unknown }>;
  };
  responses?: Record<string, { description?: string; content?: Record<string, unknown> }>;
}

export interface OpenApiDocument {
  openapi: string;
  info?: { title?: string; version?: string; summary?: string; description?: string };
  servers?: Array<{ url: string; description?: string }>;
  paths: Record<string, Record<string, OpenApiOperation>>;
  components?: {
    securitySchemes?: Record<string, unknown>;
  };
}

/** The methods the surface uses, in the order the reference lists them. */
export const OPENAPI_METHODS = ['get', 'post', 'patch', 'delete'] as const;
export type OpenApiMethod = (typeof OPENAPI_METHODS)[number];

export const OPENAPI_JSON_PATH = '/api/v1/openapi.json';
export const OPENAPI_YAML_PATH = '/api/v1/openapi.yaml';

export async function fetchOpenApiDocument(): Promise<OpenApiDocument> {
  const response = await fetch(OPENAPI_JSON_PATH, {
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`openapi.json responded ${response.status}`);
  return (await response.json()) as OpenApiDocument;
}

/** One operation, flattened out of the document's path → method nesting. */
export interface ApiOperation {
  id: string;
  method: OpenApiMethod;
  /** As written in the document — `/api/v1/quotes/{quoteId}`. */
  path: string;
  summary: string;
  tag: string;
  scope: string | null;
  parameters: OpenApiParameter[];
  requestSchema: OpenApiSchema | null;
  requestContentType: string | null;
  /** False for an action whose whole input is its path — no body at all. */
  hasRequestBody: boolean;
  /**
   * The body may not be left off. Answered by the generator against the zod
   * schema itself, so it accounts for the rules a JSON Schema `required` list
   * cannot express — a customer needs one of three names, none of them
   * individually required.
   */
  requestRequired: boolean;
  /**
   * A body the schema was checked to accept, generated alongside the document.
   * Preferred over anything this page could assemble: a rule that spans fields
   * ("an amount, unless a visit is named") is invisible in a JSON Schema and
   * decides whether the request works.
   */
  requestExample: unknown;
  successStatus: string;
  /** The response is bytes, so the console shows a note instead of a body. */
  binary: boolean;
}

/**
 * The scope an operation names, out of its `security` list.
 *
 * The document offers two ways in on every operation — a key, or an access
 * token — and the scope rides on the OAuth one, since that is the entry a
 * consent screen is built from. `apiKey: []` is not "no scope required": it is
 * "this scheme does not enumerate scopes", which is why the OAuth entry is the
 * one read here. An operation with an empty list really does need none, which
 * is true of exactly one endpoint (`/me`).
 */
function scopeOf(operation: OpenApiOperation): string | null {
  for (const requirement of operation.security ?? []) {
    for (const scopes of Object.values(requirement)) {
      if (scopes.length > 0) return scopes[0] ?? null;
    }
  }
  return null;
}

function successStatusOf(operation: OpenApiOperation): string {
  const codes = Object.keys(operation.responses ?? {});
  return codes.find((code) => code.startsWith('2')) ?? '200';
}

/** Flattens the document into the list the page renders, in document order. */
export function operationsOf(document: OpenApiDocument): ApiOperation[] {
  const operations: ApiOperation[] = [];
  for (const [path, methods] of Object.entries(document.paths ?? {})) {
    for (const method of OPENAPI_METHODS) {
      const operation = methods[method];
      if (!operation) continue;
      const status = successStatusOf(operation);
      const success = operation.responses?.[status];
      const content = operation.requestBody?.content ?? {};
      const contentType = Object.keys(content)[0] ?? null;
      operations.push({
        id: operation.operationId ?? `${method}-${path}`,
        method,
        path,
        summary: operation.summary ?? '',
        tag: operation.tags?.[0] ?? 'Other',
        scope: scopeOf(operation),
        parameters: operation.parameters ?? [],
        requestSchema: contentType ? (content[contentType]?.schema ?? null) : null,
        requestContentType: contentType,
        hasRequestBody: contentType !== null,
        requestRequired: operation.requestBody?.required === true,
        requestExample: contentType ? content[contentType]?.example : undefined,
        successStatus: status,
        binary: Object.keys(success?.content ?? {}).some((type) => !type.startsWith('application/json')),
      });
    }
  }
  return operations;
}

/** Tags in the order the document introduces them, each with its operations. */
export function groupByTag(operations: ApiOperation[]): Array<{ tag: string; operations: ApiOperation[] }> {
  const groups: Array<{ tag: string; operations: ApiOperation[] }> = [];
  for (const operation of operations) {
    let group = groups.find((candidate) => candidate.tag === operation.tag);
    if (!group) {
      group = { tag: operation.tag, operations: [] };
      groups.push(group);
    }
    group.operations.push(operation);
  }
  return groups;
}
